import httpService from "@/config/settings/axios-settings";
import { IRequestParams } from "@base/interfaces/requests/request-params.interface";
import { Response } from "@base/interfaces/requests/response.interface";
import { Icon } from "@iconify/react/dist/iconify.js";
import { App } from "antd";
import { useCallback, useMemo } from "react";

export interface IHttpRequest {
  endpoint: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: any;
  headers?: any;
  params?: IRequestParams;
  showNotificacion?: boolean;
  onSuccess?: (resp: Response) => void;
  onError?: (resp: Response) => void;
  onFinish?: (resp: Response) => void;
}

function useHttp() {
  const { modal, notification } = App.useApp();

  const showSuccessNotification = useCallback(
    (message: string) => {
      notification.success({
        message: "Éxito",
        description: message,
      });
    },
    [notification],
  );

  const showErrorNotification = useCallback(
    (message: string) => {
      notification.error({
        message: "Error",
        description: message,
      });
    },
    [notification],
  );

  function handleError(
    response: Response,
    error: any,
    showNotificacion: boolean = false,
  ): void {
    let errorMessage =
      error?.response?.data?.mensaje || "Ha ocurrido un error en la solicitud.";
    const errorDetails = error?.response?.data?.errores || "Error inesperado";

    response.errores = [errorDetails];
    response.mensaje = errorMessage;

    if (
      errorDetails &&
      errorDetails !== "Error inesperado" &&
      Object.keys(errorDetails)?.length > 0
    ) {
      errorMessage = `${errorMessage}. ${Object.values(errorDetails).join(". ")}`;
    }
    if (showNotificacion) {
      showErrorNotification(errorMessage);
    }
  }

  const handleErrorCb = useCallback(handleError, [showErrorNotification]);

  const get = useCallback(
    async function get({
      endpoint,
      params,
      onSuccess,
      onError,
      onFinish,
      showNotificacion = false,
    }: IHttpRequest): Promise<Response> {
      let response: Response = { resultado: [] };
      try {
        const { data, status } = await httpService.get<Response>(endpoint, {
          params,
        });
        response = data;
        if (status === 200) {
          if (onSuccess) onSuccess(response);
          if (showNotificacion && response.mensaje) {
            showSuccessNotification(response.mensaje);
          }
        }
      } catch (error) {
        handleErrorCb(response, error, showNotificacion);
        if (onError) onError(response);
      } finally {
        if (onFinish) onFinish(response);
      }
      return response;
    },
    [handleErrorCb, showSuccessNotification],
  );

  const post = useCallback(
    async function post({
      endpoint,
      body,
      headers,
      onSuccess,
      onError,
      onFinish,
      showNotificacion = true,
    }: IHttpRequest): Promise<Response> {
      let response: Response = { resultado: [] };
      try {
        const { data, status } = await httpService.post<Response>(
          endpoint,
          body,
          { headers },
        );
        response = data;
        if (status === 200) {
          if (onSuccess) onSuccess(response);
          if (showNotificacion && response.mensaje) {
            showSuccessNotification(response.mensaje);
          }
        }
      } catch (error) {
        handleErrorCb(response, error, showNotificacion);
        if (onError) onError(response);
      } finally {
        if (onFinish) onFinish(response);
      }
      return response;
    },
    [handleErrorCb, showSuccessNotification],
  );

  const put = useCallback(
    async function put({
      endpoint,
      body,
      headers,
      onSuccess,
      onError,
      onFinish,
      showNotificacion = true,
    }: IHttpRequest): Promise<Response> {
      let response: Response = { resultado: [] };
      try {
        const { data, status } = await httpService.put<Response>(
          endpoint,
          body,
          {
            headers,
          },
        );
        response = data;
        if (status === 200) {
          if (onSuccess) onSuccess(response);
          if (showNotificacion && response.mensaje) {
            showSuccessNotification(response.mensaje);
          }
        }
      } catch (error) {
        handleErrorCb(response, error, showNotificacion);
        if (onError) onError(response);
      } finally {
        if (onFinish) onFinish(response);
      }
      return response;
    },
    [handleErrorCb, showSuccessNotification],
  );

  const del = useCallback(
    async function del({
      endpoint,
      params,
      body,
      headers,
      onSuccess,
      onError,
      onFinish,
      showNotificacion = true,
    }: IHttpRequest): Promise<Response> {
      let response: Response = { resultado: [] };
      try {
        const { data, status } = await httpService.delete(endpoint, {
          params,
          headers,
          data: body ?? params,
        });
        response = data;
        if (status === 200) {
          if (onSuccess) onSuccess(response);
          if (showNotificacion && response.mensaje) {
            showSuccessNotification(response.mensaje);
          }
        }
      } catch (error) {
        handleErrorCb(response, error, showNotificacion);
        if (onError) onError(response);
      } finally {
        if (onFinish) onFinish(response);
      }
      return response;
    },
    [handleErrorCb, showSuccessNotification],
  );

  const downloadFile = useCallback(
    async function downloadFile({
      endpoint,
      params,
      headers,
    }: IHttpRequest): Promise<void> {
      try {
        const response = await httpService.get(endpoint, {
          params,
          headers,
          responseType: "blob",
        });

        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;

        const disposition = response.headers["content-disposition"];
        let filename = "archivo-descargado";

        if (disposition) {
          const utf8Match = disposition.match(
            /filename\*\s*=\s*UTF-8''([^;]+)/i,
          );
          if (utf8Match?.[1]) {
            filename = decodeURIComponent(utf8Match[1].trim());
          } else {
            const asciiMatch = disposition.match(
              /filename\s*=\s*"?([^";]+)"?/i,
            );
            if (asciiMatch?.[1]) {
              filename = asciiMatch[1].trim();
            }
          }
        }
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        handleErrorCb({ resultado: [] }, error, true);
      }
    },
    [handleErrorCb],
  );

  const defaultDelete = useCallback(
    function defaultDelete({
      modalContent = "¿Está seguro de querer eliminar el registro?",
      requestParams,
    }: {
      modalContent?: React.ReactNode;
      requestParams: IHttpRequest;
    }) {
      modal.confirm({
        title: "Eliminar",
        icon: (
          <Icon
            icon={"ant-design:delete-outlined"}
            className="text-[#e61b23] h-[1.5rem] w-[1.5rem] mr-1"
          />
        ),
        content: modalContent,
        okText: "Si, Eliminar",
        okButtonProps: {
          danger: true,
        },
        cancelText: "No, Cancelar",
        onOk: async () => {
          await del(requestParams);
        },
      });
    },
    [modal, del],
  );

  return useMemo(
    () => ({ get, post, put, del, downloadFile, defaultDelete }),
    [get, post, put, del, downloadFile, defaultDelete],
  );
}

export default useHttp;
